import {
    ScoreDimensionValue,
    ScoreSkeleton,
    ScoreValue,
    ScoreWithStatistics,
} from "src/api/client-api/post/scoreSchema";
import { ScoreByProperties, ScoreService } from "../../EntityBasedScore/ScoreService";
import { Post, PostElement } from "src/api/client-api/post/postSchema";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { Campaign } from "src/api/client-api/useCampaign";
import { FieldPropertyType } from "src/api/sure-api/ssq/useGetOneSsq";
import { formatUnitAbbreviation } from "src/utils/formatting";

export type PropertyValue = {
    type: "value";
    fieldType: FieldPropertyType["type"];
    value: string;
    unit: string | undefined;
    score: ScoreWithStatistics | null;
    datalistOptionIds: number[];
    isGenericValue: boolean;

    // Used only for multivalues;
    position?: number;

    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};
export type PropertyGroup = {
    type: "group";
    content: PropertyValue[];

    score: ScoreWithStatistics | null;
    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};

type PropertyMultiScore = {
    title: string;
    name?: string;
    score: ScoreWithStatistics | null;
};
export type PropertyMulti = {
    type: "multi";
    content: PropertyValue[][];
    scoreBySubproperty: PropertyMultiScore[];
    score: ScoreWithStatistics | null;
    property: SsqProperty;
    title: string;
    name: string;

    campaign: Campaign | undefined;
    postId: number;
};
export type PropertyConditional = PropertyValue | PropertyGroup | PropertyMulti;

// This is the main Element of the magic sauce.
// It contains 1 question (ssq_property) + 1 answer (element) + 1 score
// And the pair of Element + Score varies depending on the Question.Type.
// export type Property = PropertyConditional & PropertyCore;
export type Property = PropertyConditional;

// Those are the properties the user face to make
// their aggregations;
type ExtraKeys<T> = { extraIndexes: T };

export type SsqPropertyWithIndexesKey<T> = SsqProperty & ExtraKeys<T>;
export type UserProperty<T> = Property & ExtraKeys<T>;
export type ConstructorOptions<T, ScoreType> = {
    listOfProperties: SsqPropertyWithIndexesKey<T>[];
    propertiesScore: ScoreByProperties<ScoreType>;
    postElementAndCampaign: Pick<Post, "id" | "campaign" | "elements">;
    precision?: number;
};

type AllowedOperations = "sum";
type AggParams<T extends Record<keyof T, number | string>> = {
    key: keyof T;
    operation: AllowedOperations;
};
type AggregationResults = Partial<Record<AllowedOperations, ScoreValue>>;

type AggReturn<T> = {
    aggregations: AggregationResults;
    properties: UserProperty<T>[];
};

export class PropertyBuilder<
    GenericKeys extends Record<keyof GenericKeys, number | string>,
    ScoreType extends ScoreSkeleton = ScoreValue,
> {
    _properties: UserProperty<GenericKeys>[];
    _initOptions: ConstructorOptions<GenericKeys, ScoreType>;
    _precision: number;

    private operatorHandler = (config: {
        properties: UserProperty<GenericKeys>[];
        aggOption: AggParams<GenericKeys>;
    }): AggregationResults => {
        const { properties, aggOption } = config;
        const aggAcc: AggregationResults = {};
        const total = properties.reduce((innerAggAcc, property) => {
            const op = aggOption.operation;
            if (op === "sum") {
                const defaultInitialValue = null;
                const prevValue = innerAggAcc.sum ?? defaultInitialValue;
                innerAggAcc.sum = ScoreService.addScore(prevValue, property.score?.value ?? null);
            }
            return innerAggAcc;
        }, aggAcc);
        return total;
    };

    public agg(options: AggParams<GenericKeys>[]): AggregationResults[] {
        const allProperties = this.build();

        const aggregations = options.map((option) => {
            const groupKey = option.key;
            const innerGroup = Object.groupBy(allProperties, (item) => item.extraIndexes[groupKey]);

            const aggregatedPropertiesAcc: AggregationResults = {};
            const aggregatedProperties = Object.entries(innerGroup).reduce((acc, [groupKey, properties]) => {
                if (properties === undefined) return acc;

                const result = this.operatorHandler({
                    properties: properties as UserProperty<GenericKeys>[],
                    aggOption: option,
                });
                return result;
            }, aggregatedPropertiesAcc);

            return aggregatedProperties;
        });
        return aggregations;
    }

    public constructor(options: ConstructorOptions<GenericKeys, ScoreType>) {
        const { listOfProperties, postElementAndCampaign, precision = 0 } = options;
        this._precision = precision;
        this._initOptions = options;

        const formatted = listOfProperties.flatMap((property) => {
            if (property.hidden === true) {
                return [];
            }
            const { extraIndexes: _, ...ssqProperty } = property;
            const validProperty = this.buildBaseProperty(ssqProperty);

            const result: UserProperty<GenericKeys> = {
                // campaign: postElementAndCampaign.campaign,
                extraIndexes: property.extraIndexes,
                ...validProperty,
            };
            return [result];
        });
        this._properties = formatted;
    }

    public build(): UserProperty<GenericKeys>[] {
        return [...this._properties];
    }

    private extractMetadataFromProperty(property: SsqProperty) {
        const titleFallback = "";
        const nameFallback = property.title ?? "";

        return {
            title: property.title ?? titleFallback,
            name: property.name ?? nameFallback,
        };
    }

    private buildBaseProperty(property: SsqProperty) {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;

        // Multi should be a `field.type` but isn't today...
        const multiKey = "multi" as const;
        const groupKey = "group" as const;
        const valueKey = "value" as const;

        // Most important condition of the Property feature
        // The main problem with this is that we would love to use `.field.type`
        // But the data is inconsistent;
        const whichPropertyIsIt =
            property.multivalue === true ? multiKey
            : (property.children ?? []).length > 0 ? groupKey
            : valueKey;

        const elementFound = elements?.find((element) => {
            return element.ssqPropertyId === property.id;
        });
        const scoreProperty = propertiesScore[property.id];

        if (whichPropertyIsIt === "value") return this.buildValueProperty(property, elementFound, scoreProperty);
        if (whichPropertyIsIt === "group") return this.buildGroupProperty(property);
        if (whichPropertyIsIt === "multi") return this.buildMultiProperty(property);

        // I would suggest to have an automatic handler that sends
        // throwed Errors to Sentry immediately, and simply restart the App
        // with a Snackbar message to notify user something went wrong.
        // But we have started an investigation automatically.

        // And here, I would take the bet this error never arrives.
        // But if it does, I'm notified on Sentry. And I have a graceful termination
        throw new Error("buildBaseProperty went wrong");
    }

    private buildValueProperty(
        property: SsqProperty,
        element: PostElement | undefined,
        scoreProperty: ScoreDimensionValue<ScoreType> | undefined,
    ): PropertyValue {
        const propertyMeta = this.extractMetadataFromProperty(property);

        if (element === undefined) {
            return {
                // Context Data
                postId: this._initOptions.postElementAndCampaign.id,

                property: property,
                title: propertyMeta.title,
                name: propertyMeta.name,

                isGenericValue: false,
                campaign: this._initOptions.postElementAndCampaign.campaign,
                type: "value",
                fieldType: "text",
                value: "",
                score: null,
                unit: "",
                datalistOptionIds: [],
            };
        }

        const fieldType = property.field?.type as FieldPropertyType["type"] | undefined;
        const defaultType = "text" as const;
        const precision = this._precision;

        const score = scoreProperty?.score;
        return {
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,

            // Meta Data
            type: "value",
            property: property,
            name: propertyMeta.name,
            title: propertyMeta.title,

            fieldType: fieldType ?? defaultType,
            unit: formatUnitAbbreviation(property.unit),

            // Data
            value: element.value,
            datalistOptionIds: element.dataListOptionIds ?? [],
            isGenericValue: element.isGenericValue ?? false,
            position: element.position,

            // Results
            score: ScoreService.normalizeScore(score, precision),
        };
    }

    private buildGroupProperty(property: SsqProperty): PropertyGroup {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;
        const propertyMeta = this.extractMetadataFromProperty(property);

        const propertyScore = propertiesScore[property.id];
        const data = property.children?.flatMap((childProperty) => {
            if (childProperty.hidden === true) {
                return [];
            }
            const elementFromChildren = elements?.find((element) => {
                return element.ssqPropertyId === childProperty.id;
            });
            const scoreDrilldown = propertyScore?.dimensions?.find((dimension) => dimension.label === "subproperties");
            const subpropertyScore = scoreDrilldown?.values[childProperty.id];

            const valueProperty = this.buildValueProperty(childProperty, elementFromChildren, subpropertyScore);
            return [valueProperty];
        });
        const precision = this._precision;

        const score = propertyScore?.score;
        return {
            type: "group",
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,

            // Meta Data
            property: property,
            title: propertyMeta.title,
            name: propertyMeta.name,

            // Data
            content: data ?? [],

            // Results
            score: ScoreService.normalizeScore(score, precision),
        };
    }

    private buildMultiProperty(property: SsqProperty): PropertyMulti {
        const {
            postElementAndCampaign: { elements },
            propertiesScore,
        } = this._initOptions;
        const propertyMeta = this.extractMetadataFromProperty(property);

        const propertyScore = propertiesScore[property.id];

        const allSubpropertiesIds = property.children?.map((propertyChild) => propertyChild.id);
        const allElementsBySubpropertiesIds = elements?.filter((element) =>
            allSubpropertiesIds?.includes(element.ssqPropertyId),
        );
        const maxPossiblePosition =
            allElementsBySubpropertiesIds?.reduce((acc, element) => {
                if (element.position > acc) return element.position;
                return acc;
            }, 0) ?? 0;
        const elementsByPosition = Object.groupBy(allElementsBySubpropertiesIds ?? [], (elem) => elem.position);

        // This seems like a really strange weird of handling the positions, RIGHT ?!
        // It's because the `multivalues` key starts from 1...
        const allPossiblePositions = Array(maxPossiblePosition + 1)
            .fill(null)
            .map((_, index) => index);

        const groupedScoreByIdOnly = propertyScore?.dimensions?.find(
            (dimension) => dimension.label === "subproperties",
        );
        const data: PropertyValue[][] = allPossiblePositions.map((position) => {
            const _elements = elementsByPosition[position];
            const groupedValuesForMulti: PropertyValue[] =
                property.children?.map((subproperty) => {
                    const _groupedScoreByPosition = groupedScoreByIdOnly?.values[subproperty.id];
                    const groupedScoreByPosition = _groupedScoreByPosition?.dimensions?.find(
                        (dimension) => dimension.label === "multivalues",
                    )?.values;

                    // This seems like a really strange weird of handling the positions, RIGHT ?!
                    // It's because the `multivalues` key starts from 1...
                    const scoreForMulti = groupedScoreByPosition?.[position + 1];
                    const foundElementInsideMulti = _elements?.find((elem) => elem.ssqPropertyId === subproperty.id);
                    const propertyValue = this.buildValueProperty(subproperty, foundElementInsideMulti, scoreForMulti);

                    return propertyValue;
                }) ?? [];
            return groupedValuesForMulti;
        });

        const scoreBySubproperty =
            property.children?.map((subProperty) => {
                const subPropertyScore = groupedScoreByIdOnly?.values[subProperty.id];
                const item: PropertyMultiScore = {
                    score: ScoreService.normalizeScore(subPropertyScore?.score, this._precision),
                    title: subProperty.title ?? "",
                    name: subProperty.name,
                };
                return item;
            }) ?? [];

        const score = propertyScore?.score;
        return {
            type: "multi",
            // Context Data
            postId: this._initOptions.postElementAndCampaign.id,
            campaign: this._initOptions.postElementAndCampaign.campaign,

            // Meta Data
            property: property,
            name: propertyMeta.name,
            title: propertyMeta.title,

            // Data
            content: data,

            // Results
            score: ScoreService.normalizeScore(score, this._precision),
            scoreBySubproperty: scoreBySubproperty,
        };
    }
}
